import { useState, useMemo, useEffect } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Dropzone from "@components/ui/blocks/Dropzone";
import DropDown from "@components/ui/dropdown/DropDown";
import { Switch } from "@components/ui/selection/Switch";
import CheckBox from "@components/ui/selection/CheckBox";
import { showNotification } from "context/notification.service";
import { estimateExtractionApi } from "@services/api/estimateExtraction";
import DevisSendPreviewModal from "./DevisSendPreviewModal";
import type { Ordre } from "types/ordre";
import type {
  EstimateExtraction,
  EstimateExtractionItem,
} from "types/estimateExtraction";

interface Props {
  ordre: Ordre;
  group?: string;
  initialExtraction?: EstimateExtraction;
  isExtracting?: boolean;
  onClose: () => void;
}

type Step = "loading" | "upload" | "selection" | "result";

const TVA_OPTIONS = [
  { value: "5.5", label: "5,5 %" },
  { value: "10", label: "10 %" },
  { value: "20", label: "20 %" },
];

function formatPrice(v: number | null | undefined): string {
  if (v == null) return "-";
  return v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " \u20AC";
}

function getItemNet(item: EstimateExtractionItem): number {
  return item.total_after_remise ?? item.total ?? 0;
}

export default function EstimateExtractionModal({ ordre, group = "initial", initialExtraction, isExtracting, onClose }: Props) {
  const [step, setStep] = useState<Step>(
    isExtracting ? "loading" : initialExtraction ? "selection" : "upload",
  );
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [extraction, setExtraction] = useState<EstimateExtraction | null>(initialExtraction || null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    if (initialExtraction?.items?.length) return new Set(initialExtraction.items.map((i) => i.id));
    return new Set();
  });
  // Everything is mandatory by default; the operator opts individual items OUT to make them optional.
  const [obligatoireIds, setObligatoireIds] = useState<Set<string>>(() => {
    if (initialExtraction?.items?.length) return new Set(initialExtraction.items.map((i) => i.id));
    return new Set();
  });

  // When extraction arrives after the modal was opened in "loading" mode, hydrate state and switch step.
  useEffect(() => {
    if (initialExtraction && step === "loading") {
      setExtraction(initialExtraction);
      setSelectedIds(new Set(initialExtraction.items.map((i) => i.id)));
      setObligatoireIds(new Set(initialExtraction.items.map((i) => i.id)));
      setStep("selection");
    }
  }, [initialExtraction, step]);
  const [tvaRate, setTvaRate] = useState("20");
  const [devisUrl, setDevisUrl] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showPreview, setShowPreview] = useState(false);

  // --- Upload ---
  const handleUpload = async () => {
    if (files.length === 0) return;
    setLoading(true);
    try {
      const result = await estimateExtractionApi.extract(ordre.id, files[0], group);
      setExtraction(result);
      if (result.status === "completed" && result.items.length > 0) {
        const allIds = new Set(result.items.map((i) => i.id));
        setSelectedIds(allIds);
        setObligatoireIds(new Set(allIds)); // All mandatory by default; operator opts items out
        setStep("selection");
      } else if (result.status === "failed") {
        showNotification({ type: "error", message: result.error || "Extraction échouée" });
      } else {
        showNotification({ type: "error", message: "Aucun poste extrait du document" });
      }
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Erreur lors de l'extraction" });
    } finally {
      setLoading(false);
    }
  };

  // --- Selection logic ---
  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleObligatoire = (id: string) => {
    setObligatoireIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!extraction) return;
    if (selectedIds.size === extraction.items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(extraction.items.map((i) => i.id)));
    }
  };

  // Totals
  const { totalHT, totalRemise, totalHTNet, totalTVA, totalTTC } = useMemo(() => {
    if (!extraction) return { totalHT: 0, totalRemise: 0, totalHTNet: 0, totalTVA: 0, totalTTC: 0 };
    let ht = 0;
    let htNet = 0;
    let tva = 0;
    let ttc = 0;
    const rate = parseFloat(tvaRate);
    for (const item of extraction.items) {
      if (selectedIds.has(item.id)) {
        ht += item.total || 0;
        htNet += getItemNet(item);
        // AlphaDMS: TVA per unit, then * qty, remise per sub-item
        let itemTva = 0;
        let itemTtc = 0;
        if (item.forfait_ttc != null) {
          // Forfait: fixed package TTC from ServiceBox; per-line VAT rounding can't reproduce it.
          const net = getItemNet(item);
          itemTtc = item.forfait_ttc;
          itemTva = Math.round((item.forfait_ttc - net) * 100) / 100;
        } else if (item.sub_items.length > 0) {
          for (const sub of item.sub_items) {
            const subRemise = sub.remise ?? 0;
            if (sub.unit_price != null && sub.quantity != null) {
              const tvaPerUnit = Math.round(sub.unit_price * rate) / 100;
              let subTva = tvaPerUnit * sub.quantity;
              let subTtc = (sub.unit_price + tvaPerUnit) * sub.quantity;
              if (subRemise > 0) {
                subTva = Math.round(subTva * (1 - subRemise / 100) * 100) / 100;
                subTtc = subTtc * (1 - subRemise / 100);
              }
              itemTva += subTva;
              itemTtc += subTtc;
            } else if (sub.total != null) {
              // total is already post-remise
              const subTva = Math.round(sub.total * rate) / 100;
              itemTva += subTva;
              itemTtc += sub.total + subTva;
            }
          }
        } else {
          const net = getItemNet(item);
          itemTva = Math.round(net * rate) / 100;
          itemTtc = net + itemTva;
        }
        // Fallback: item-level remise (legacy extractions) — forfait postes carry no remise
        if (item.forfait_ttc == null && item.remise != null && item.remise > 0) {
          itemTva = Math.round(itemTva * (1 - item.remise / 100) * 100) / 100;
          itemTtc = itemTtc * (1 - item.remise / 100);
        }
        tva += itemTva;
        ttc += Math.round(itemTtc * 100) / 100;
      }
    }
    const remise = Math.round((ht - htNet) * 100) / 100;
    tva = Math.round(tva * 100) / 100;
    ttc = Math.round(ttc * 100) / 100;
    return { totalHT: ht, totalRemise: remise, totalHTNet: htNet, totalTVA: tva, totalTTC: ttc };
  }, [extraction, selectedIds, tvaRate]);

  // --- Send to client: save config + create token ---
  const handleSendToClient = async (remindersEnabled?: boolean, disabledReminders?: string[]) => {
    if (!extraction || selectedIds.size === 0) return;
    setShowPreview(false);
    setLoading(true);
    try {
      const { url } = await estimateExtractionApi.createToken(extraction.id, {
        selected_item_ids: Array.from(selectedIds),
        obligatoire_item_ids: Array.from(obligatoireIds),
        tva_rate: parseFloat(tvaRate),
        reminders_enabled: remindersEnabled,
        disabled_reminders: disabledReminders,
      });
      setDevisUrl(url);
      navigator.clipboard.writeText(url);
      showNotification({ type: "success", message: "Lien du devis copié !" });
      setStep("result");
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Erreur lors de la création du lien" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <BaseModal
      title="Générer un devis"
      startIcon={<Icon1 name="file" size={20} />}
      onClose={onClose}
      width="max-w-4xl"
    >
      {/* Step 0: Loading skeleton (waiting for extraction) */}
      {step === "loading" && (
        <>
          <div className="p-4">
            <p className="text-h3 text-neutral-500 mb-3">
              Récupération du devis en cours…
            </p>
            <div className="flex flex-row items-center justify-between mb-3">
              <div className="h-5 w-32 rounded bg-neutral-100 animate-pulse" />
              <div className="h-8 w-24 rounded bg-neutral-100 animate-pulse" />
            </div>
            <div className="flex flex-col gap-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="border-1 rounded-lg overflow-hidden"
                >
                  <div className="flex items-center gap-3 px-3 py-3 bg-neutral-50">
                    <div className="h-4 w-4 rounded bg-neutral-200 animate-pulse shrink-0" />
                    <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                      <div className="h-4 rounded bg-neutral-200 animate-pulse" style={{ width: `${50 + ((i * 13) % 35)}%` }} />
                      <div className="h-3 w-20 rounded bg-neutral-100 animate-pulse" />
                    </div>
                    <div className="h-6 w-24 rounded bg-neutral-100 animate-pulse shrink-0" />
                    <div className="h-5 w-16 rounded bg-neutral-200 animate-pulse shrink-0" />
                    <div className="h-5 w-16 rounded bg-neutral-100 animate-pulse shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
          </div>
        </>
      )}

      {/* Step 1: Upload */}
      {step === "upload" && (
        <>
          <div className="p-4">
            <p className="text-h3 text-neutral-500 mb-3">
              Déposez le devis fournisseur (PDF) pour extraire automatiquement les postes.
            </p>
            <Dropzone
              onDrop={setFiles}
              files={files}
              setSelectedDroppedFiles={setFiles}
              onDelete={(file) => setFiles(files.filter((f) => f !== file))}
              acceptTypes={{ "application/pdf": [".pdf"] }}
            />
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
            <Button
              variant="small"
              label={loading ? "Extraction en cours..." : "Extraire les postes"}
              onClick={handleUpload}
              disabled={files.length === 0 || loading}
              loadingBar={loading}
            />
          </div>
        </>
      )}

      {/* Step 2: Selection */}
      {step === "selection" && extraction && (
        <>
          <div className="p-4">
            {/* Toolbar */}
            <div className="flex flex-row items-center justify-between mb-3">
              <button
                className="text-h4 text-blue-600 hover:underline cursor-pointer"
                onClick={toggleAll}
              >
                {selectedIds.size === extraction.items.length ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
              <div className="flex items-center gap-2">
                <span className="text-h4 text-neutral-500">TVA :</span>
                <div style={{ width: 100 }}>
                  <DropDown
                    options={TVA_OPTIONS}
                    value={tvaRate}
                    onSelect={(v) => setTvaRate(v as string)}
                  />
                </div>
              </div>
            </div>

            {/* Items list */}
            <div className="flex flex-col gap-2">
              {extraction.items.map((item) => {
                const selected = selectedIds.has(item.id);
                const isObligatoire = obligatoireIds.has(item.id);
                const expanded = expandedIds.has(item.id);
                return (
                  <div
                    key={item.id}
                    className={`border-1 rounded-lg overflow-hidden transition-opacity ${selected ? "" : "opacity-40"}`}
                  >
                    {/* Item header */}
                    <div
                      className="flex items-center gap-3 px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-blue-50"
                      onClick={() => toggleItem(item.id)}
                    >
                      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                        <CheckBox
                          checked={selected}
                          onChange={() => toggleItem(item.id)}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-h3 font-medium truncate">{item.libelle}</div>
                        {item.sub_items.length > 0 && (
                          <span className="text-h5 text-neutral-400">{item.sub_items.length} sous-postes</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {/* Obligatoire switch */}
                        <div
                          className="flex items-center gap-1.5"
                          style={{ width: 110 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Switch
                            size="small"
                            checked={isObligatoire}
                            onChange={() => toggleObligatoire(item.id)}
                          />
                          <span className={`text-h5 ${isObligatoire ? "text-neutral-700" : "text-neutral-400"}`}>
                            {isObligatoire ? "Obligatoire" : "Optionnel"}
                          </span>
                        </div>
                        {/* Price HT */}
                        <div className="text-right" style={{ width: 90 }}>
                          <div className="text-h3 font-semibold">{formatPrice(getItemNet(item))}</div>
                          {item.remise != null && item.remise > 0 && (
                            <div className="text-h5 text-red-500">-{item.remise}%</div>
                          )}
                        </div>
                        {/* Price TTC */}
                        <div className="text-right" style={{ width: 90 }}>
                          <div className="text-h5 text-neutral-400">TTC</div>
                          <div className="text-h3 font-semibold">
                            {(() => {
                              // Forfait: show the authoritative package TTC from ServiceBox.
                              if (item.forfait_ttc != null) return formatPrice(item.forfait_ttc);
                              const rate = parseFloat(tvaRate);
                              let ttc = 0;
                              if (item.sub_items.length > 0) {
                                for (const sub of item.sub_items) {
                                  const subRemise = sub.remise ?? 0;
                                  if (sub.unit_price != null && sub.quantity != null) {
                                    const tvaPerUnit = Math.round(sub.unit_price * rate) / 100;
                                    let subTtc = (sub.unit_price + tvaPerUnit) * sub.quantity;
                                    if (subRemise > 0) subTtc *= (1 - subRemise / 100);
                                    ttc += subTtc;
                                  } else if (sub.total != null) {
                                    ttc += sub.total + Math.round(sub.total * rate) / 100;
                                  }
                                }
                              } else {
                                const net = getItemNet(item);
                                ttc = net + Math.round(net * rate) / 100;
                              }
                              if (item.remise != null && item.remise > 0) {
                                ttc = ttc * (1 - item.remise / 100);
                              }
                              return formatPrice(Math.round(ttc * 100) / 100);
                            })()}
                          </div>
                        </div>
                        {/* Expand arrow — always rendered for alignment */}
                        <div style={{ width: 32 }} className="shrink-0 flex items-center justify-center">
                          {item.sub_items.length > 0 && (
                            <button
                              className="text-neutral-400 hover:text-neutral-600 p-2 -m-2 rounded hover:bg-neutral-100"
                              onClick={(e) => { e.stopPropagation(); toggleExpand(item.id); }}
                            >
                              <Icon1 name={expanded ? "up" : "down"} size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Sub-items (collapsible) */}
                    {expanded && item.sub_items.length > 0 && (
                      <div className="border-t-1">
                        <table className="w-full">
                          <tbody>
                            {item.sub_items.map((sub, i) => {
                              const subRemise = sub.remise ?? 0;
                              let subTtc: number | null = null;
                              if (sub.unit_price != null && sub.quantity != null) {
                                const tvaPerUnit = Math.round(sub.unit_price * parseFloat(tvaRate)) / 100;
                                subTtc = (sub.unit_price + tvaPerUnit) * sub.quantity;
                                if (subRemise > 0) subTtc *= (1 - subRemise / 100);
                                subTtc = Math.round(subTtc * 100) / 100;
                              } else if (sub.total != null) {
                                subTtc = Math.round((sub.total + Math.round(sub.total * parseFloat(tvaRate)) / 100) * 100) / 100;
                              }
                              return (
                              <tr key={i} className="border-b-1 border-neutral-100 last:border-b-0">
                                <td className="px-3 py-1.5 text-h4">
                                  <span className={`inline-block text-h5 px-1.5 py-0 rounded mr-2 ${
                                    sub.type === "piece" ? "bg-indigo-50 text-indigo-600" : "bg-amber-50 text-amber-700"
                                  }`}>
                                    {sub.type === "piece" ? "PR" : "MO"}
                                  </span>
                                  {sub.libelle}
                                  {subRemise > 0 && (
                                    <span className="inline-block text-h5 px-1.5 py-0 rounded ml-2 bg-red-50 text-red-500">
                                      -{subRemise}%
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-1.5 text-h4 text-right text-neutral-400 whitespace-nowrap">
                                  {sub.quantity != null ? sub.quantity : "-"} {sub.unit || ""}
                                </td>
                                <td className="px-2 py-1.5 text-h4 text-right whitespace-nowrap" style={{ width: 80 }}>
                                  {formatPrice(sub.unit_price)}
                                </td>
                                <td className="px-2 py-1.5 text-h4 text-right font-medium whitespace-nowrap" style={{ width: 80 }}>
                                  {formatPrice(sub.total)}
                                </td>
                                <td className="px-3 py-1.5 text-h4 text-right font-medium whitespace-nowrap text-neutral-400" style={{ width: 90 }}>
                                  {subTtc != null ? formatPrice(subTtc) : "-"}
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* TTC mismatch warning */}
          {extraction.grand_total_ttc != null && selectedIds.size === extraction.items.length && Math.abs(totalTTC - extraction.grand_total_ttc) > 0.05 && (
            <div className="mx-4 mb-0 px-3 py-2 bg-amber-50 border-1 border-amber-200 rounded-lg text-h4 text-amber-800">
              Le TTC calculé ({formatPrice(totalTTC)}) ne correspond pas au TTC du document original ({formatPrice(extraction.grand_total_ttc)}). Veuillez vérifier les montants extraits.
            </div>
          )}

          {/* Bottom bar */}
          <div className="border-t-1 p-4 bg-neutral-50">
            <div className="flex flex-row items-center justify-between">
              <div className="flex flex-row gap-6 text-h3">
                <span>Total HT : <strong>{formatPrice(totalHT)}</strong></span>
                {totalRemise > 0 && (
                  <span>Remise : <strong className="text-red-500">-{formatPrice(totalRemise)}</strong></span>
                )}
                <span>TVA : <strong>{formatPrice(totalTVA)}</strong></span>
                <span>Total TTC : <strong className="text-blue-600">{formatPrice(totalTTC)}</strong></span>
              </div>
              <div className="flex flex-row gap-2">
                <SecondaryButton variant="small" onClick={() => setStep("upload")}>Retour</SecondaryButton>
                <Button
                  variant="small"
                  label={loading ? "Envoi..." : "Envoyer au client"}
                  onClick={() => setShowPreview(true)}
                  disabled={selectedIds.size === 0 || loading}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Step 3: Result — link sent */}
      {step === "result" && (
        <>
          <div className="p-4 flex flex-col items-center gap-6 py-8">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <Icon1 name="done2" size={24} color="#059669" />
            </div>
            <p className="text-h2 font-medium">Lien du devis créé</p>
            <p className="text-h3 text-neutral-500">Le lien a été copié dans votre presse-papier.</p>
            {devisUrl && (
              <div
                className="bg-neutral-50 border-1 rounded-lg px-4 py-3 text-h4 text-neutral-600 w-full text-center cursor-pointer hover:bg-neutral-100"
                onClick={() => {
                  navigator.clipboard.writeText(devisUrl);
                  showNotification({ type: "success", message: "Lien copié !" });
                }}
              >
                {devisUrl}
              </div>
            )}
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
            <Button variant="small" label="Fermer" onClick={onClose} />
          </div>
        </>
      )}
    </BaseModal>

    {showPreview && (
      <DevisSendPreviewModal
        onClose={() => setShowPreview(false)}
        onConfirm={(remindersEnabled, disabledReminders) =>
          handleSendToClient(remindersEnabled, disabledReminders)
        }
        receptionDate={
          ordre.reception_date
            ? new Date(ordre.reception_date.seconds * 1000)
            : null
        }
        clientEmail={
          ordre.devis_signer_email ||
          ordre.contacts?.[0]?.email ||
          ""
        }
        loading={loading}
      />
    )}
    </>
  );
}
